import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission } from '@shared/types';
import { Button } from '../components/Button';
import { EmptyState, Spinner } from '../components/Card';
import { useAuthStore } from './authStore';

export function FullPageLoading({ label = '正在加载…' }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center gap-3 bg-surface-0 text-sm text-fg-muted">
      <Spinner /> {label}
    </div>
  );
}

/** 未登录一律踢回登录页，并记住来源以便登录后跳回。 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  if (status === 'loading') return <FullPageLoading label="正在校验会话…" />;
  if (status === 'anonymous') return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <>{children}</>;
}

export interface RequirePermissionProps {
  /** 需要全部满足 */
  permissions?: readonly Permission[];
  /** 满足其一即可 */
  anyOf?: readonly Permission[];
  children: ReactNode;
}

/**
 * 体验层权限闸门。
 *
 * 只负责「不给用户看到用不了的入口」，绝不能当作安全边界 ——
 * 真正的鉴权在 Worker 的 requirePermission 里强制执行。
 */
export function RequirePermission({ permissions, anyOf, children }: RequirePermissionProps) {
  const user = useAuthStore((state) => state.user);
  const granted = useAuthStore((state) => state.permissions);

  if (!user) return <Navigate to="/login" replace />;

  const required = [...(permissions ?? []), ...(anyOf ?? [])];
  if (required.length === 0) return <>{children}</>;

  const allOk = (permissions ?? []).every((permission) => granted.includes(permission));
  const anyOk = (anyOf ?? []).length === 0 || anyOf!.some((permission) => granted.includes(permission));
  if (allOk && anyOk) return <>{children}</>;

  return (
    <div className="p-6">
      <EmptyState
        title="没有访问该页面的权限"
        description={`当前角色 ${user.role} 缺少所需权限：${required.join('、')}。如需访问请联系平台管理员调整角色。`}
        action={
          <Button variant="default" onClick={() => window.history.back()}>
            返回上一页
          </Button>
        }
      />
    </div>
  );
}
